import type { Components, Theme } from '@mui/material/styles';

import { MIN_TOUCH_TARGET, radius, shadow, statusTag } from './tokens';

/** Decisions made once, so no component has to remember them. A repeated `sx`
 *  prop across files belongs here instead. */
export const components: Components<Theme> = {
  MuiCssBaseline: {
    styleOverrides: {
      '#root': { isolation: 'isolate' },
    },
  },

  MuiButton: {
    defaultProps: {
      disableElevation: true,
      disableTouchRipple: false,
    },
    styleOverrides: {
      root: {
        borderRadius: radius.md,
        minHeight: MIN_TOUCH_TARGET,
        textTransform: 'none',
        fontWeight: 600,
      },
      sizeSmall: { minHeight: 36 },
    },
  },

  MuiIconButton: {
    styleOverrides: {
      root: {
        minWidth: MIN_TOUCH_TARGET,
        minHeight: MIN_TOUCH_TARGET,
      },
    },
  },

  MuiTextField: {
    defaultProps: {
      fullWidth: true,
      variant: 'outlined',
    },
  },

  MuiInputBase: {
    styleOverrides: {
      input: {
        // iOS Safari zooms the viewport when a focused input is under 16px.
        // This is the value the user TYPES and must never shrink; the hint text
        // below is a separate rule precisely so it can.
        fontSize: 16,
        // Placeholders are a hint, not content. At the input's own size they
        // compete with real values and make an empty form look filled in.
        // Shrinking only `::placeholder` leaves the typed text at 16px, so the
        // iOS zoom guarantee above still holds.
        '&::placeholder': { fontSize: 14, opacity: 0.7 },
      },
    },
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: { borderRadius: radius.md },
    },
  },

  // Field labels sit in the same visual register as placeholders — chrome
  // around the value rather than the value itself.
  MuiInputLabel: {
    styleOverrides: {
      root: { fontSize: 14 },
      // The floated label shrinks again via `transform: scale(0.75)`, which
      // would take 14px to ~10.5px and below the legible floor. Holding it at
      // 1 and letting the smaller base size do the work keeps it readable.
      shrink: { transform: 'translate(14px, -9px) scale(1)', fontSize: 12 },
    },
  },

  MuiFormHelperText: {
    styleOverrides: {
      root: { fontSize: 12 },
    },
  },

  MuiSelect: {
    styleOverrides: {
      select: { fontSize: 16 },
    },
  },

  MuiPaper: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: { backgroundImage: 'none' },
      rounded: { borderRadius: radius.lg },
    },
  },

  MuiCard: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: radius.lg,
        border: `1px solid ${theme.palette.divider}`,
      }),
    },
  },

  MuiDialog: {
    styleOverrides: {
      paper: ({ theme }) => ({
        borderRadius: radius.lg,
        // Full-bleed on phones; a centred modal wastes a third of the screen.
        [theme.breakpoints.down('sm')]: {
          margin: 0,
          width: '100%',
          maxWidth: '100%',
          maxHeight: '100%',
          borderRadius: 0,
        },
      }),
    },
  },

  MuiMenu: {
    styleOverrides: {
      paper: ({ theme }) => ({
        // The border is what defines the edge against a white surface, where
        // a shadow alone leaves it undefined.
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: shadow.menu,
        marginTop: 6,
        marginBottom: 6,
      }),
    },
  },

  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        minHeight: MIN_TOUCH_TARGET,
        borderRadius: radius.sm,
        marginInline: 6,
        fontSize: 14,
        '&:hover': { backgroundColor: theme.palette.brand[50] },
      }),
    },
  },

  MuiListItemButton: {
    styleOverrides: {
      root: { minHeight: MIN_TOUCH_TARGET, borderRadius: radius.md },
    },
  },

  MuiAvatar: {
    styleOverrides: {
      // Opt out of the global tabular-nums so initials stay centred.
      root: { fontVariantNumeric: 'normal', letterSpacing: '0.01em' },
    },
  },

  MuiTabs: {
    styleOverrides: {
      // MUI's scroll arrows are 40px wide, under the touch minimum. They also
      // reserve their width while disabled, which leaves a gap at each end of a
      // strip that fits; collapsing them to zero hands that space back to the
      // labels.
      scrollButtons: {
        width: MIN_TOUCH_TARGET,
        '&.Mui-disabled': { width: 0, opacity: 0, transition: 'width 150ms' },
      },
    },
  },

  MuiTab: {
    styleOverrides: {
      root: { minHeight: MIN_TOUCH_TARGET, textTransform: 'none', fontWeight: 600 },
    },
  },

  MuiTooltip: {
    defaultProps: {
      // Tooltips do not exist on touch — use helper text for anything real.
      enterTouchDelay: 400,
    },
  },

  MuiChip: {
    styleOverrides: {
      root: { borderRadius: radius.sm, fontWeight: 600 },
    },
  },

  /**
   * Alerts carry the status-tag palette rather than MUI's own derivation.
   *
   * By default MUI builds an alert's fill by lightening `palette.warning.main`
   * to roughly 8% — on this app's muted status ramp that lands a hair off white,
   * and an overdue-payment warning that a director has to notice ends up paler
   * than the card it sits on. The tag ramp is the one already used for exactly
   * this job at chip size, so an alert and the status chip beside it agree.
   *
   * Shadowed AND bordered. A pale fill on a pale canvas has almost no luminance
   * separation of its own, so the shadow is what makes the banner read as a
   * surface sitting forward of the page; the border only closes its top edge,
   * where a drop shadow contributes nothing.
   *
   * That border is the fill nudged 4% toward its own ink — about 1.05:1 against
   * the fill, a seam rather than an outline, and roughly a third the strength of
   * the tag `edge` rung. `edge` was tried here and is too strong at this size: it
   * is sized for a chip a few characters wide, and around a three-line paragraph
   * the same contrast draws a box that reads as a form field. Derived from the
   * fill rather than hardcoded so a token change carries through. The icon keeps the ink colour, so the alert still carries
   * its meaning without relying on the fill alone (§11).
   *
   * Slots are `colorWarning` etc., not the `standardWarning` of earlier MUI —
   * v9 splits variant and colour into separate classes.
   */
  MuiAlert: {
    defaultProps: { variant: 'standard' },
    styleOverrides: {
      root: {
        borderRadius: radius.md,
        borderWidth: 0.5,
        borderStyle: 'solid',
        boxShadow: shadow.card,
        fontWeight: 500,
      },
      colorWarning: {
        backgroundColor: statusTag.warning.fill,
        borderColor: `color-mix(in srgb, ${statusTag.warning.ink} 4%, ${statusTag.warning.fill})`,
        color: statusTag.warning.ink,
        '& .MuiAlert-icon': { color: statusTag.warning.ink },
      },
      colorError: {
        backgroundColor: statusTag.danger.fill,
        borderColor: `color-mix(in srgb, ${statusTag.danger.ink} 4%, ${statusTag.danger.fill})`,
        color: statusTag.danger.ink,
        '& .MuiAlert-icon': { color: statusTag.danger.ink },
      },
      colorSuccess: {
        backgroundColor: statusTag.success.fill,
        borderColor: `color-mix(in srgb, ${statusTag.success.ink} 4%, ${statusTag.success.fill})`,
        color: statusTag.success.ink,
        '& .MuiAlert-icon': { color: statusTag.success.ink },
      },
      // Grey, not the `progress` blue. An info alert here says "this will become
      // your default card" or "you have no saved cards" — an aside, not a state
      // the reader is waiting on. Colouring it would put it in competition with
      // the overdue warning it can appear beside, and the palette's `info` is
      // deliberately greyscale for exactly this reason.
      colorInfo: {
        backgroundColor: statusTag.neutral.fill,
        borderColor: `color-mix(in srgb, ${statusTag.neutral.ink} 4%, ${statusTag.neutral.fill})`,
        color: statusTag.neutral.ink,
        '& .MuiAlert-icon': { color: statusTag.neutral.ink },
      },
    },
  },

  MuiLink: {
    defaultProps: { underline: 'hover' },
  },
};
